import React, { useState } from 'react';
import { ChevronDownIcon, RulerIcon } from 'lucide-react';
const legend: {
  term: string;
  meaning: string;
}[] = [{
  term: 'Le',
  meaning: 'Length of the waterline, measured bow to stern at water level (m).'
}, {
  term: 'Bf',
  meaning: 'Width at 1/4 of the length, measured from the front (m).'
}, {
  term: 'Bm',
  meaning: 'Width at the middle (1/2 of the length) — the widest point (m).'
}, {
  term: 'Bb',
  meaning: 'Width at 1/4 of the length, measured from the back (m).'
}, {
  term: 'De',
  meaning: 'Vertical distance from the fixed X-point down to the waterline with the boat EMPTY (cm).'
}, {
  term: 'Dl',
  meaning: 'Vertical distance from the same X-point down to the waterline with the boat LOADED (cm).'
}];
export function BoatMeasurementGuide() {
  const [open, setOpen] = useState(true);
  return <section className="border border-blue-200 bg-blue-50/60 rounded-lg overflow-hidden" data-id="element-114" data-mp="1q0a73pey7x53">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left" data-id="element-115" data-mp="elbl6p6lea8o">
        <span className="flex items-center gap-2 font-semibold text-blue-900 text-sm" data-id="element-116" data-mp="oihdg0qy07ah">
          <RulerIcon size={16} data-id="element-117" data-mp="t9fyfdmvltk2" />
          Measurement reference (Appendix G.2, Figure 2)
        </span>
        <ChevronDownIcon size={18} className={`text-blue-700 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-118" data-mp="y9c199ju7ey1" />
      </button>

      {open && <div className="px-4 pb-4 grid gap-4 lg:grid-cols-2" data-id="element-119" data-mp="12c8lt5sxtl4m">
          {/* Schematic */}
          <div className="bg-white rounded-lg border border-blue-100 p-3" data-id="element-120" data-mp="177o0fzyrrl23">
            <svg viewBox="0 0 320 200" role="img" aria-label="Diagram of a boat hull from above and in section, showing where Le, Bf, Bm, Bb, De and Dl are measured" className="w-full h-auto" data-id="element-121" data-mp="1c81zwtmb1moi">
              {/* Plan view */}
              <text x="8" y="14" fontSize="9" fill="#1e3a8a" fontWeight="700" data-id="element-122" data-mp="35zr5u6eop5k4">
                PLAN VIEW
              </text>
              <path d="M30 60 C70 26, 230 26, 290 60 C230 94, 70 94, 30 60 Z" fill="#eff6ff" stroke="#1d4ed8" strokeWidth="1.5" data-id="element-123" data-mp="32008tkykdt75" />
              {/* Le */}
              <line x1="30" y1="104" x2="290" y2="104" stroke="#1d4ed8" strokeWidth="1" markerStart="url(#arrow)" markerEnd="url(#arrow)" data-id="element-124" data-mp="3oxlak88s4uwp" />
              <text x="150" y="116" fontSize="9" fill="#1d4ed8" fontWeight="700" data-id="element-125" data-mp="3totjz4a40g9v">
                Le
              </text>
              {/* Width markers */}
              {[{
            x: 95,
            label: 'Bf',
            top: 36,
            bottom: 84
          }, {
            x: 160,
            label: 'Bm',
            top: 30,
            bottom: 90
          }, {
            x: 225,
            label: 'Bb',
            top: 36,
            bottom: 84
          }].map(m => <g key={m.label} data-id="element-126" data-mp="m2r2wrmn71pb">
                  <line x1={m.x} y1={m.top} x2={m.x} y2={m.bottom} stroke="#dc2626" strokeWidth="1.2" data-id="element-127" data-mp="7ffqyomjtfoo" />
                  <text x={m.x - 6} y={m.top - 4} fontSize="9" fill="#dc2626" fontWeight="700" data-id="element-128" data-mp="3ul6omzge6glf">
                    {m.label}
                  </text>
                </g>)}

              {/* Section view */}
              <text x="8" y="140" fontSize="9" fill="#1e3a8a" fontWeight="700" data-id="element-129" data-mp="3pn54scb56d8w">
                SECTION AT Bm
              </text>
              <path d="M70 150 L250 150 L215 192 L105 192 Z" fill="#eff6ff" stroke="#1d4ed8" strokeWidth="1.5" data-id="element-130" data-mp="rrpkx4cmm8hs" />
              {/* X point */}
              <circle cx="160" cy="150" r="3" fill="#111827" data-id="element-131" data-mp="i0mo4eqqcfob" />
              <text x="166" y="148" fontSize="8" fill="#111827" fontWeight="700" data-id="element-132" data-mp="d2l6ly8ycoey">
                X-point (gunwale)
              </text>
              {/* Empty waterline */}
              <line x1="80" y1="166" x2="240" y2="166" stroke="#0ea5e9" strokeWidth="1" strokeDasharray="4 3" data-id="element-133" data-mp="932lb1d7ybbl" />
              <text x="244" y="169" fontSize="7.5" fill="#0ea5e9" data-id="element-134" data-mp="1ygftj1ogcjhz">
                empty WL
              </text>
              {/* Loaded waterline */}
              <line x1="88" y1="182" x2="232" y2="182" stroke="#047857" strokeWidth="1" strokeDasharray="4 3" data-id="element-135" data-mp="1thmdb1hs6ee4" />
              <text x="236" y="185" fontSize="7.5" fill="#047857" data-id="element-136" data-mp="2aqf8o99aqn29">
                loaded WL
              </text>
              {/* De / Dl */}
              <line x1="130" y1="150" x2="130" y2="166" stroke="#0ea5e9" strokeWidth="1.2" data-id="element-137" data-mp="2fnx9tdh2nxzp" />
              <text x="112" y="162" fontSize="9" fill="#0ea5e9" fontWeight="700" data-id="element-138" data-mp="1n1311dtman22">
                De
              </text>
              <line x1="190" y1="150" x2="190" y2="182" stroke="#047857" strokeWidth="1.2" data-id="element-139" data-mp="1s1qs1zhuj3wa" />
              <text x="194" y="172" fontSize="9" fill="#047857" fontWeight="700" data-id="element-140" data-mp="33gxbraa0sofo">
                Dl
              </text>
              <defs data-id="element-141" data-mp="18bed5wny2tbt">
                <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto" data-id="element-142" data-mp="18av4l6k6phag">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#1d4ed8" data-id="element-143" data-mp="1hbt0t2odskih" />
                </marker>
              </defs>
            </svg>
          </div>

          {/* Legend */}
          <dl className="space-y-2" data-id="element-144" data-mp="pkn837azpp38">
            {legend.map(item => <div key={item.term} className="flex gap-3" data-id="element-145" data-mp="km27v7vqn5es">
                <dt className="w-9 flex-shrink-0 font-bold text-blue-900 text-sm" data-id="element-146" data-mp="knn36zpvmsdp">
                  {item.term}
                </dt>
                <dd className="text-xs text-blue-900/80 leading-relaxed" data-id="element-147" data-mp="tmryzya207m6">
                  {item.meaning}
                </dd>
              </div>)}
            <p className="text-xs text-blue-900/70 pt-1 border-t border-blue-200" data-id="element-148" data-mp="5vmj5j445fbv">
              Water displacement <strong data-id="element-149" data-mp="1v2l8dbpu7oo">Dis = De − Dl</strong>. Both De and Dl
              must be taken from the same fixed X-point on the gunwale.
            </p>
          </dl>
        </div>}
    </section>;
}